import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Trash2, ExternalLink, ShieldCheck } from "lucide-react";
import { PageHeader, Panel, Badge, Select, fmtD } from "@/components/admin/ui";
import { UploadButton } from "./LessonEditor";

const TYPES = [["servsafe", "ServSafe Certificate"], ["food_handler", "Food Handler Card"], ["id", "Photo ID"], ["w4", "W-4"], ["i9", "I-9"], ["direct_deposit", "Direct Deposit"], ["work_agreement", "Work Agreement"], ["other", "Other"]];

export default function StoreDocuments() {
  const { sid } = useParams();
  const { user } = useAuth();
  const manage = ["super_admin", "admin", "manager"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [emps, setEmps] = useState([]);
  const [f, setF] = useState({ type: "servsafe", user_id: "", expires_at: "" });

  const load = useCallback(() => { api.get(`/stores/${sid}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([])); }, [sid]);
  useEffect(load, [load]);
  useEffect(() => { if (manage) api.get(`/employees?store_id=${sid}`).then((r) => setEmps(r.data)).catch(() => {}); }, [sid, manage]);

  const onUploaded = async (d) => {
    try {
      await api.post(`/stores/${sid}/documents`, { type: f.type, user_id: f.user_id || null, file_url: d.url, filename: d.filename, expires_at: f.expires_at || null });
      toast.success("Document saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (d) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${d.id}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const expired = (d) => d.expires_at && d.expires_at < new Date().toISOString().slice(0, 10);

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Store · Team" title={manage ? "Employee Documents" : "My Documents"} testId="documents-title"
        subtitle={manage ? "Certificates and employment paperwork for this location." : "Upload your ServSafe certificate and required employment documents."}/>
      <Panel className="p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="doc-upload-form">
        <div><Label>Document type</Label><Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="doc-type">{TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></div>
        {manage && <div><Label>Employee</Label><Select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="doc-employee"><option value="">Myself</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select></div>}
        <div><Label>Expires (optional)</Label><Input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className="h-10"/></div>
        <UploadButton kind="documents" accept="image/*,application/pdf" label="Upload document" onUploaded={onUploaded} testId="doc-upload"/>
      </Panel>
      <Panel className="divide-y divide-neutral-100">
        {docs.length === 0 && <p className="p-10 text-center text-sm text-neutral-500" data-testid="docs-empty">No documents uploaded yet.</p>}
        {docs.map((d) => (
          <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`doc-${d.id}`}>
            <div className="w-9 h-9 rounded-lg bg-neutral-100 flex items-center justify-center">{d.type === "servsafe" ? <ShieldCheck className="w-4 h-4 text-emerald-600"/> : <FileText className="w-4 h-4"/>}</div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">{d.title}{manage && <span className="text-neutral-400 font-normal"> · {d.user_name}</span>}</div>
              <div className="text-xs text-neutral-500">{d.filename} · {fmtD(d.created_at)}{d.expires_at && <> · expires {fmtD(d.expires_at)}</>}</div>
            </div>
            {expired(d) && <Badge tone="red">expired</Badge>}
            <a href={d.file_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5"/></Button></a>
            <button onClick={() => remove(d)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
          </div>
        ))}
      </Panel>
    </div>
  );
}
